import { TimePicker } from "@msys/ui";
import { FormControl, TextFieldProps } from "@mui/material";
import { useField } from "formik";
import { isString } from "lodash-es";
import moment, { type Moment } from "moment/moment.js";
import { ChangeEvent } from "react";
import { browserHasInputTime } from "../../featureDetection.js";

function toMoment(value: string | null | undefined): Moment | null {
  if (!value) return null;
  const m = moment(value, "HH:mm");
  return m.isValid() ? m : null;
}

function toTimeString(value: Moment | null): string | null {
  if (!value) return null;
  return value.format("HH:mm");
}

export interface Props extends Omit<TextFieldProps, "value" | "onChange"> {
  id?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  name: string;
  label: string;
  value?: string | null;
  required?: boolean;
  onChange?: (
    e: ChangeEvent<HTMLInputElement> | null,
    newValue: string | null
  ) => void | Promise<void>;
}

export const TimePickerField = ({
  fullWidth = true,
  disabled,
  label,
  name,
  value: valueProp,
  onChange: onChangeProp,
  ...otherProps
}: Props) => {
  const [{ value }, { error }, { setValue }] = useField<string | null>(name);

  const valueMoment = toMoment(valueProp !== undefined ? valueProp : value);
  const onChangeMoment = async (
    e: ChangeEvent<HTMLInputElement> | null,
    newValue: Moment | null
  ) => {
    const newValueString = toTimeString(newValue);
    if (onChangeProp) {
      await onChangeProp(e, newValueString);
    } else {
      await setValue(newValueString);
    }
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      required={otherProps.required}
      error={Boolean(error)}
      disabled={disabled}
    >
      <TimePicker
        browserHasInputTime={browserHasInputTime}
        error={Boolean(error)}
        label={label}
        disabled={disabled}
        value={valueMoment}
        slotProps={{
          inputLabel: {
            shrink: true,
          },
          input: {
            inputProps: {
              step: 60, // 1 min
            },
          },
        }}
        onChange={onChangeMoment}
        helperText={Boolean(error) && isString(error) ? error : undefined}
        {...otherProps}
      />
    </FormControl>
  );
};
